import clsx from 'clsx'
import { useState, type ReactNode } from 'react'
import { View } from 'react-native'

import { ActionSheet } from '@/src/elements/action'
import { Button } from '@/src/elements/button/default'
import { Card, CardBody, CardFooter, CardHeader } from '@/src/elements/card'
import { CheckStamp, type TCheckstampProps } from '@/src/elements/checkstamp/Checkstamp'
import { Message } from '@/src/elements/message'
import { GradientTypography } from '@/src/elements/text/gradient'
import { Typography } from '@/src/elements/text/Typography'
import { ABUSE_TYPE } from '@/src/features/abuse/models/Abuse.enums'
import { ReportAbuseModal } from '@/src/features/abuse/ui/ReportAbuseModal/ReportAbuseModal'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import { CreateUpliftModal } from '@/src/features/intention/ui/screens/IntentionPrayersScreen/components/CreateUpliftModal'
import { useToast } from '@/src/general/components/ToastContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

import { useRefuseIntentionWatchmanMutation } from '../../../services/useRefuseIntentionWatchmanMutation'
import { WatchmanSummaryPlaceholder } from './WatchmanSummaryPlaceholder'

export type TWatchmanSummaryProps = {
  userName: string
  intention?: string
  isPrayed?: boolean
  uplift?: { message: string; user: { id: number } }
  birthdayDate?: string | null
  type: INTENTION_TYPE
  intentionId: number
  id: number
  prayerId?: number
} & Pick<TCheckstampProps, 'checkAnimationDelay'>

const PARTY_EMOJI = ' 🥳'

export const WatchmanSummary = ({
  checkAnimationDelay,
  userName,
  intention,
  isPrayed,
  uplift,
  type,
  birthdayDate,
  prayerId,
  intentionId,
  id,
}: TWatchmanSummaryProps): ReactNode => {
  const [showModal, setShowModal] = useState(false)
  const [showUpliftModal, setShowUpliftModal] = useState(false)
  const { t } = useTranslate()
  const { addToast } = useToast()
  const { mutate, isPending } = useRefuseIntentionWatchmanMutation()

  const handleReplaceIntention = () => mutate({ id })

  const getText = () => {
    switch (type) {
      case INTENTION_TYPE.INTENTION:
        return intention
      case INTENTION_TYPE.ARRIVAL:
        return t('intention.arrival.welcome-pray')
      case INTENTION_TYPE.BIRTHDAY:
        return t('intention.birthday.pray')
      default:
        return ''
    }
  }

  const birthdayTitle = (name: string, birthday: string) => {
    return t('intention.birthday.celebration', {
      username: name,
      years: new Date(Date.now()).getFullYear() - new Date(birthday).getFullYear(),
    })
  }

  const isPrayedTypedIntention = isPrayed && (type === INTENTION_TYPE.BIRTHDAY || type === INTENTION_TYPE.ARRIVAL)

  if (isPending) return <WatchmanSummaryPlaceholder />

  return (
    <Card
      className={clsx('max-w-full min-w-0 gap-3 p-1', { 'gap-2': !isPrayed })}
      testID={TEST_ID.WATCHMAN.SUMMARY.DEFAULT}
    >
      <CardHeader
        className={clsx('flex min-w-0 flex-row items-center justify-between gap-3 pb-0', {
          'pb-3': isPrayedTypedIntention,
        })}
      >
        {isPrayed && <CheckStamp state="valid" checkAnimationDelay={checkAnimationDelay} />}
        {!(type === INTENTION_TYPE.BIRTHDAY) && (
          <Typography bold numberOfLines={2} className="min-w-0 flex-1 shrink overflow-hidden break-words">
            {type === INTENTION_TYPE.INTENTION && userName}
            {type === INTENTION_TYPE.ARRIVAL && t('intention.arrival.just-joined', { userName })}
          </Typography>
        )}
        {type === INTENTION_TYPE.BIRTHDAY && birthdayDate && (
          <View className="min-w-0 flex-1 shrink overflow-hidden">
            <GradientTypography
              variant="gradient"
              multicolor
              bold
              numberOfLines={2}
              className="min-w-0 break-words"
              classNameWrapper="min-w-0 native:flex-row native:items-start native:justify-start"
              unaffectedChildren={
                <Typography
                  ariaRole="span"
                  bold
                  className="native:flex-none"
                  role="img"
                  aria-label={t('user.happy-birthday.wish')}
                >
                  {PARTY_EMOJI}
                </Typography>
              }
            >
              {birthdayTitle(userName, birthdayDate)}
            </GradientTypography>
          </View>
        )}
        <View className="shrink-0 flex-row items-center gap-0">
          {type === INTENTION_TYPE.INTENTION && (
            <>
              <ActionSheet items={[{ children: t('app.report-abuse.intention'), onPress: () => setShowModal(true) }]}>
                <Button
                  testID={TEST_ID.WATCHMAN.SUMMARY.BTN_MENU}
                  isIconOnly
                  startIcon={ICON.ELLIPSIS_VERTICAL}
                  variant="light"
                  colors="default"
                  size="sm"
                />
              </ActionSheet>
              <ReportAbuseModal
                showModal={showModal}
                onClose={() => setShowModal(false)}
                objectId={intentionId}
                objectType={ABUSE_TYPE.INTENTION}
                text={intention ?? ''}
                onDone={() => {
                  addToast({
                    message: t('app.report-abuse.intention.success'),
                    colors: 'success',
                    iconName: ICON.MESSAGE_INFO,
                  })
                }}
              />
            </>
          )}
          {!isPrayed && (
            <Button
              testID={TEST_ID.WATCHMAN.SUMMARY.REFRESH_BTN}
              isIconOnly
              startIcon={ICON.REFRESH}
              variant="light"
              colors="default"
              size="sm"
              onPress={handleReplaceIntention}
            />
          )}
        </View>
      </CardHeader>
      {!isPrayedTypedIntention && (
        <CardBody className={clsx('pt-0', { 'pb-0': isPrayed })}>
          <Typography colors={type === INTENTION_TYPE.INTENTION ? 'default' : 'primary'}>{getText()}</Typography>
        </CardBody>
      )}
      {prayerId && (
        <CreateUpliftModal
          showModal={showUpliftModal}
          onClose={() => setShowUpliftModal(false)}
          onConfirm={() => {
            setShowUpliftModal(false)
          }}
          intentionId={intentionId}
          intention={intention}
          userName={userName}
          prayerId={prayerId}
        />
      )}
      {isPrayed && type === INTENTION_TYPE.INTENTION && (
        <CardFooter className="flex flex-row justify-end pt-0">
          {uplift?.message ? (
            <Message colors="primary" iconName={ICON.MESSAGE_HEART}>
              {uplift.message}
            </Message>
          ) : (
            <Button
              onPress={() => setShowUpliftModal(true)}
              startIcon={ICON.PENCIL}
              variant="faded"
              colors="primary"
              size="sm"
            >
              {t('uplift.message')}
            </Button>
          )}
        </CardFooter>
      )}
    </Card>
  )
}
